import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radar, ArrowLeft, MessageSquarePlus, ExternalLink } from "lucide-react";
import AnnouncementChyron from "@/components/AnnouncementChyron";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { SocialChips } from "@/components/atfq/SocialChips";
import { VoteButton } from "@/components/atfq/VoteButton";
import { fetchVoiceArchive } from "@/lib/api";

const PILLARS = ["Science", "Politics", "Philosophy", "Religion"];

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";

const VoiceCard = ({ v }) => (
  <article data-testid={`voice-archive-${v.id}`} className="border-t border-slate-200 pt-6 flex flex-col">
    <div className="flex items-center justify-between text-xs">
      <span className="text-[var(--accent)]">{v.pillars.join(" · ")}</span>
      {v.featured_now && <span data-testid="voice-archive-live" className="border border-[var(--accent)] text-[var(--accent)] px-2 py-0.5">In rotation</span>}
    </div>
    <h3 className="mt-3 font-display text-2xl text-[#0A192F] leading-tight tracking-tight">{v.name}</h3>
    <p className="mt-1 text-sm text-slate-600">{v.role}{v.affiliation ? ` · ${v.affiliation}` : ""}</p>
    <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-4">{v.blurb}</p>
    {v.question && (
      <p className="mt-3 text-sm text-[#0A192F] italic border-l-2 border-[var(--accent)] pl-3">&ldquo;{v.question}&rdquo;</p>
    )}
    <SocialChips socials={v.socials} className="mt-4" />
    <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
      <VoteButton voice={v} />
      <Link
        to={`/?guest=${encodeURIComponent(v.name)}#atfq-ask`}
        data-testid="voice-archive-ask"
        className="inline-flex items-center gap-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white px-3 py-2 font-medium transition-colors"
      >
        <MessageSquarePlus className="w-3.5 h-3.5" /> What should Vic ask?
      </Link>
      {v.sources?.[0] && (
        <a href={v.sources[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-500 hover:text-[var(--accent)]">
          Source <ExternalLink className="w-3 h-3" />
        </a>
      )}
      <span className="ml-auto text-slate-400">Featured {fmt(v.featured_at)}</span>
    </div>
  </article>
);

export default function VoicesPage() {
  const [data, setData] = useState(null);
  const [pillar, setPillar] = useState("All");

  useEffect(() => {
    fetchVoiceArchive().then(setData).catch(() => setData({ total: 0, counts: {}, voices: [] }));
  }, []);

  const voices = (data?.voices || []).filter((v) => pillar === "All" || v.pillars.includes(pillar));

  return (
    <main className="bg-white text-[#0A192F] min-h-screen">
      <AnnouncementChyron />
      <Nav />
      <section data-testid="voices-page" className="pt-40 md:pt-48 pb-24">
        <div className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#0A192F] mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to ATFQ
          </Link>
          <div className="text-xs tracking-tight text-[var(--accent)] mb-3 flex items-center gap-2">
            <Radar className="w-3.5 h-3.5" /> Voices We&apos;ve Watched
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            Every voice ATFQ has put on the watchlist.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed max-w-2xl">
            Academics, officials, writers, and clergy we think can answer the f**king question.
            Four rotate onto the home page every Monday, Wednesday, and Friday — this is the full archive.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-slate-500">
          <span>Four rotate onto the home page Mon · Wed · Fri</span>
          <span>· Vote for who you want on the mic — Vic sees the leaderboard every Sunday</span>
        </div>

        <div className="mt-12 flex flex-wrap gap-1" data-testid="voices-filter">
            {["All", ...PILLARS].map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`voices-filter-${p.toLowerCase()}`}
                onClick={() => setPillar(p)}
                aria-pressed={pillar === p}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  pillar === p ? "bg-[#0A192F] border-[#0A192F] text-white" : "border-slate-300 text-slate-500 hover:text-[#0A192F]"
                }`}
              >
                {p}{p !== "All" && data?.counts?.[p] != null ? ` · ${data.counts[p]}` : p === "All" && data ? ` · ${data.total}` : ""}
              </button>
            ))}
          </div>

          {data && voices.length === 0 ? (
            <p data-testid="voices-empty" className="mt-12 text-sm text-slate-500 border-t border-slate-200 pt-8">
              Nothing here yet — check back after the next rotation.
            </p>
          ) : (
            <div data-testid="voices-grid" className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {voices.map((v) => <VoiceCard key={v.id} v={v} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
